// Header del portale clienti — logo FieldCore, nome azienda, lingua, logout.
// Layout separato dall'admin: minimal, bianco, senza sidebar.
import { useNavigate } from 'react-router-dom'
import { LogOut, Globe, Menu, X } from 'lucide-react'
import { useState } from 'react'
import { usePortalStore } from '../portal.store'
import { cn } from '../../../lib/utils'

interface PortalHeaderProps {
  className?: string
}

export function PortalHeader({ className }: PortalHeaderProps) {
  const navigate = useNavigate()
  const { companyName, email, clearPortalAuth } = usePortalStore()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleLogout = () => {
    clearPortalAuth()
    navigate('/portal/login')
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full border-b border-gray-200 bg-white shadow-sm',
        className
      )}
      role="banner"
    >
      <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
        {/* Logo + brand */}
        <div
          className="flex cursor-pointer items-center gap-3"
          onClick={() => navigate('/portal/dashboard')}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/portal/dashboard')}
          aria-label="Vai alla dashboard"
        >
          <img src="/logo.png" alt="KleisNote" className="h-12 w-auto" />
          <div>
            <span className="block text-sm font-semibold text-gray-900">KleisNote</span>
            <span className="block text-xs text-gray-500">Portale Clienti</span>
          </div>
        </div>

        {/* Navigazione desktop */}
        <nav className="hidden items-center gap-6 md:flex" aria-label="Navigazione portale">
          <a
            className="cursor-pointer text-sm text-gray-600 transition-colors hover:text-[var(--fc-primary)]"
            onClick={() => navigate('/portal/dashboard')}
          >
            Dashboard
          </a>
          <a
            className="cursor-pointer text-sm text-gray-600 transition-colors hover:text-[var(--fc-primary)]"
            onClick={() => navigate('/portal/tickets')}
          >
            I miei ticket
          </a>
          <a
            className="cursor-pointer text-sm text-gray-600 transition-colors hover:text-[var(--fc-primary)]"
            onClick={() => navigate('/portal/reports')}
          >
            Rapportini
          </a>
          <a
            className="cursor-pointer text-sm text-gray-600 transition-colors hover:text-[var(--fc-primary)]"
            onClick={() => navigate('/portal/calendar')}
          >
            Prenota
          </a>
        </nav>

        {/* Destra: azienda + logout */}
        <div className="hidden items-center gap-3 md:flex">
          {companyName && (
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--fc-primary)] text-xs font-semibold text-white">
                {companyName.slice(0, 2).toUpperCase()}
              </div>
              <div className="max-w-[160px]">
                <p className="truncate text-sm font-medium text-gray-900">{companyName}</p>
                {email && (
                  <p className="truncate text-xs text-gray-500">{email}</p>
                )}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
            aria-label="Esci dal portale"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Esci
          </button>
        </div>

        {/* Bottone menu mobile */}
        <button
          type="button"
          className="flex items-center justify-center rounded-lg p-2 text-gray-600 hover:bg-gray-100 md:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Chiudi menu' : 'Apri menu'}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen
            ? <X className="h-5 w-5" aria-hidden="true" />
            : <Menu className="h-5 w-5" aria-hidden="true" />}
        </button>
      </div>

      {/* Menu mobile */}
      {mobileMenuOpen && (
        <div className="border-t border-gray-100 bg-white px-4 pb-4 md:hidden">
          <nav className="flex flex-col gap-2 pt-2" aria-label="Navigazione portale mobile">
            {[
              { label: 'Dashboard', path: '/portal/dashboard' },
              { label: 'I miei ticket', path: '/portal/tickets' },
              { label: 'Rapportini', path: '/portal/reports' },
              { label: 'Prenota manutenzione', path: '/portal/calendar' },
            ].map((item) => (
              <button
                key={item.path}
                type="button"
                className="rounded-lg px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => {
                  navigate(item.path)
                  setMobileMenuOpen(false)
                }}
              >
                {item.label}
              </button>
            ))}
            <div className="my-2 border-t border-gray-100" />
            {companyName && (
              <div className="px-3 py-1">
                <p className="text-sm font-medium text-gray-900">{companyName}</p>
                {email && <p className="text-xs text-gray-500">{email}</p>}
              </div>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Esci dal portale
            </button>
          </nav>
        </div>
      )}
    </header>
  )
}
